import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const UpcomingAppointments = ({ appointments, onReschedule, onCancel }) => {
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'in-progress':
        return 'text-primary bg-primary/10 border-primary/20';
      default:
        return 'text-text-secondary bg-muted border-border';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`)?.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-elevation-2 border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">
            Upcoming Appointments
          </h2>
          <span className="text-sm text-text-secondary">
            {appointments?.length} scheduled
          </span>
        </div>
      </div>
      <div className="p-6">
        {appointments?.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Calendar" size={32} color="var(--color-text-secondary)" />
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">
              No upcoming appointments
            </h3>
            <p className="text-text-secondary mb-6">
              Schedule a service to see your appointments here
            </p>
            <Button variant="default" iconName="Plus" iconPosition="left">
              Book Service
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {appointments?.map((appointment) => (
              <div
                key={appointment?.id}
                className="border border-border rounded-lg p-4 hover:shadow-elevation-1 transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-text-primary">
                        {appointment?.service}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(appointment?.status)}`}>
                        {appointment?.status?.charAt(0)?.toUpperCase() + appointment?.status?.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-text-secondary mb-3">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={16} />
                        <span>{formatDate(appointment?.date)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={16} />
                        <span>{formatTime(appointment?.time)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPin" size={16} />
                        <span>{appointment?.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-text-primary">
                      ${appointment?.price}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {appointment?.duration}
                    </div>
                  </div>
                </div>

                {/* Technician Info */}
                {appointment?.technician && (
                  <div className="flex items-center space-x-3 mb-4 p-3 bg-muted rounded-lg">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <Image
                        src={appointment?.technician?.avatar}
                        alt={appointment?.technician?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-text-primary">
                        {appointment?.technician?.name}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {appointment?.technician?.rating} ★ • {appointment?.technician?.experience}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="MessageCircle"
                      iconPosition="left"
                    >
                      Contact
                    </Button>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Calendar"
                      iconPosition="left"
                      onClick={() => onReschedule(appointment?.id)}
                    >
                      Reschedule
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      iconPosition="left"
                      onClick={() => onCancel(appointment?.id)}
                      className="text-error hover:text-error"
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={() => setSelectedAppointment(appointment)}
                    >
                      View Details
                    </Button>
                    {appointment?.status === 'confirmed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Edit"
                        iconPosition="left"
                      >
                        Modify
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingAppointments;